import React, { useEffect, useState, type FormEvent } from "react";
import { getClientIdentityHeaders } from "@/lib/clientIdentity";
import type { ExposureReactionDossierCaptureV3 } from "@shared/exposureReactionDossier";
import ExposureReactionDossierDisplay, { type ExposureReactionDossierVersionSummary } from "@/components/ExposureReactionDossierDisplay";

type Candidate = {
  eventRevisionKey: string;
  provider: string;
  reportId: string;
  revisionId: string;
  title: string;
  sentAt: string | null;
  updatedAt: string | null;
  geometryAvailable: boolean;
};
type CreateResponse = {
  dossier: { id: string; currentReportId: string; questionId: string };
  report: { id: string; title?: string };
  capture: ExposureReactionDossierCaptureV3;
  manifestDigest: string;
  version: ExposureReactionDossierVersionSummary;
};
type Failure = { message: string; retryable: boolean };
type CandidateListState = "ready" | "missing" | "unavailable" | "failed" | "empty" | "truncated";

function uuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  throw new Error("This browser cannot create a secure request ID. Update the browser and try again.");
}

async function responseBody(response: Response): Promise<Record<string, unknown>> {
  try {
    const value: unknown = await response.json();
    return value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
  } catch { return {}; }
}

function failure(status: number, payload: Record<string, unknown>): Failure {
  const detail = typeof payload.detail === "string" ? payload.detail : typeof payload.error === "string" ? payload.error : null;
  return { message: status === 0 ? "No HTTP response was received. The save result is unknown." : `Exposure dossier request failed with HTTP ${status}${detail ? `: ${detail}` : "."}`, retryable: status === 0 || status >= 500 };
}

function readCandidates(value: unknown): Candidate[] | null {
  if (!Array.isArray(value)) return null;
  const candidates: Candidate[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object" || Array.isArray(item)) return null;
    const row = item as Record<string, unknown>;
    if (typeof row.eventRevisionKey !== "string" || typeof row.provider !== "string" || typeof row.reportId !== "string"
      || typeof row.revisionId !== "string" || typeof row.title !== "string" || typeof row.geometryAvailable !== "boolean"
      || !(row.sentAt === null || typeof row.sentAt === "string") || !(row.updatedAt === null || typeof row.updatedAt === "string")) return null;
    candidates.push({ eventRevisionKey: row.eventRevisionKey, provider: row.provider, reportId: row.reportId, revisionId: row.revisionId, title: row.title,
      sentAt: row.sentAt as string | null, updatedAt: row.updatedAt as string | null, geometryAvailable: row.geometryAvailable });
  }
  return candidates;
}

const ExposureReactionDossierEntry: React.FC = () => {
  const [enabled, setEnabled] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [candidateState, setCandidateState] = useState<"loading" | "ready" | "empty" | "unavailable">("loading");
  const [selectedKey, setSelectedKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<{ eventRevisionKey: string; requestId: string } | null>(null);
  const [saved, setSaved] = useState<CreateResponse | null>(null);
  const [error, setError] = useState<Failure | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/research-dossiers/exposure/config", { headers: getClientIdentityHeaders(), credentials: "include", cache: "no-store", signal: controller.signal })
      .then(async (response) => ({ response, payload: response.ok ? await responseBody(response) : null }))
      .then(({ payload: config }) => {
        if (controller.signal.aborted) return;
        if (!config) { setCandidateState("unavailable"); return; }
        const isEnabled = config.enabled === true;
        setEnabled(isEnabled);
        if (!isEnabled) { setCandidateState("unavailable"); return; }
        return fetch("/api/research-dossiers/exposure/events", { headers: getClientIdentityHeaders(), credentials: "include", cache: "no-store", signal: controller.signal })
          .then(async (response) => ({ response, payload: await responseBody(response) }))
          .then(({ response, payload }) => {
            if (controller.signal.aborted) return;
            if (!response.ok) { setCandidateState("unavailable"); return; }
            const state = payload.state as CandidateListState | undefined;
            if (state === "empty") {
              setCandidates([]);
              setSelectedKey("");
              setCandidateState("empty");
              return;
            }
            if (state !== "ready") { setCandidateState("unavailable"); return; }
            const rows = readCandidates(payload.events);
            if (!rows) { setCandidateState("unavailable"); return; }
            setCandidates(rows);
            setSelectedKey(rows[0]?.eventRevisionKey ?? "");
            setCandidateState(rows.length > 0 ? "ready" : "empty");
          });
      })
      .catch(() => { if (!controller.signal.aborted) { setEnabled(false); setCandidateState("unavailable"); } });
    return () => controller.abort();
  }, []);

  const sendAssessment = async (request: { eventRevisionKey: string; requestId: string }) => {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch("/api/research-dossiers/exposure", {
        method: "POST", credentials: "include",
        headers: { ...getClientIdentityHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(request),
      });
      const payload = await responseBody(response);
      if (!response.ok) { setError(failure(response.status, payload)); return; }
      if (!payload.capture || !payload.dossier || !payload.report || !payload.version) {
        setError({ message: "The server returned an incomplete save response. The result may have been saved; retry with the same request ID.", retryable: true });
        return;
      }
      setSaved(payload as unknown as CreateResponse);
      setPending(null);
    } catch { setError(failure(0, {})); }
    finally { setBusy(false); }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!selectedKey || busy || (pending && error?.retryable)) return;
    try {
      const request = { eventRevisionKey: selectedKey, requestId: uuid() };
      setPending(request);
      void sendAssessment(request);
    } catch (cause) { setError({ message: cause instanceof Error ? cause.message : "Could not create a secure request ID.", retryable: false }); }
  };

  if (!enabled) return null;

  return <section className="analytics-panel-section" aria-labelledby="exposure-dossier-entry-title">
    <div className="analytics-command">
      <div>
        <p className="home-kicker">POTENTIAL EXPOSURE · SAMPLED RESPONSE · ZERO MODEL CALLS</p>
        <h2 id="exposure-dossier-entry-title">Potential exposure and separately observed market response</h2>
        <p className="home-summary">Assess a retained event revision against the reviewed facility point and exact derivative instrument. Exposure, sampled price movement, loss, and causation remain separate.</p>
      </div>
      {candidateState === "loading" && <p role="status">Loading eligible retained event revisions…</p>}
      {candidateState === "unavailable" && <p role="status">Eligible event revisions could not be read. No dossier was created.</p>}
      {candidateState === "empty" && <p role="status">No eligible retained event revisions are available. No dossier was created.</p>}
      {candidateState === "ready" && candidates.length > 0 && <form className="home-symbol-form analytics-form" onSubmit={submit} aria-label="Create an exposure and reaction dossier">
        <label htmlFor="exposure-event-revision">Retained event revision</label>
        <select id="exposure-event-revision" value={selectedKey} onChange={(event) => setSelectedKey(event.target.value)} disabled={busy}>
          {candidates.map((candidate) => <option key={candidate.eventRevisionKey} value={candidate.eventRevisionKey}>
            {candidate.provider} · {candidate.title} · {candidate.reportId} / {candidate.revisionId}
          </option>)}
        </select>
        {candidates.find((candidate) => candidate.eventRevisionKey === selectedKey)?.geometryAvailable === false
          && <p>Full geometry is not available for this saved revision. Exposure and response will remain unavailable where evidence is missing.</p>}
        <button type="submit" className="analytics-action-btn" disabled={busy || !selectedKey || (!!pending && error?.retryable === true)}>
          {busy ? "Saving assessment…" : "Create saved assessment"}
        </button>
      </form>}
      {error && <div className="analytics-error" role="alert" aria-live="assertive">
        <p>{error.message}</p>
        {error.retryable && pending && <button type="button" className="analytics-action-btn" onClick={() => void sendAssessment(pending)} disabled={busy}>Retry the same request</button>}
      </div>}
      {saved && <div aria-live="polite">
        <p>Saved exposure dossier version {saved.version.sequence}. No model call was made.</p>
        <a className="analytics-action-btn" href={`/reports?id=${encodeURIComponent(saved.report.id)}`}>Open saved dossier in Reports</a>
        <ExposureReactionDossierDisplay capture={saved.capture} versions={[saved.version]} />
      </div>}
    </div>
  </section>;
};

export default ExposureReactionDossierEntry;
